import React, { useEffect, useState } from 'react'
import n4hLogo from '../../assets/logo.png'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from '../../components/toast/CustomToast'
import api from '../../utils/api'

const LoginPage = ({ onLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const { isDarkMode } = useAuth();

    useEffect(() => {
        const savedLogin = localStorage.getItem('n4h_remember_login');
        if (savedLogin) {
            try {
                const { email: savedEmail, password: savedPassword } = JSON.parse(savedLogin);
                setEmail(savedEmail);
                setPassword(savedPassword);
                setRememberMe(true);
            } catch (err) {}
        } else if (import.meta.env.DEV) {
            const defaultEmail = import.meta.env.VITE_ADMIN_EMAIL;
            if (defaultEmail) {
                setEmail(defaultEmail);
            }
        }
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');

        if (!email.trim()) { setError('Por favor, informe seu email'); return; }
        if (!password.trim()) { setError('Por favor, informe sua senha'); return; }

        try {
            const response = await api.post('/auth/login', { email, password });

            if (rememberMe) {
                localStorage.setItem('n4h_remember_login', JSON.stringify({ email, password }));
            } else {
                localStorage.removeItem('n4h_remember_login');
            }

            onLogin(response.data.access_token, email);
            toast.success('Login realizado com sucesso');
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Credenciais inválidas';
            setError(errorMessage);
            toast.error(errorMessage);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: '#0A1C28' }}>
            {/* Background blobs */}
            <div className="absolute top-0 -left-40 w-96 h-96 rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ backgroundColor: '#25C6A4' }}></div>
            <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ backgroundColor: '#25C6A4', animationDelay: '2s' }}></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full filter blur-3xl opacity-5 animate-pulse" style={{ backgroundColor: '#25C6A4', animationDelay: '4s' }}></div>

            {/* Main card */}
            <div
                className="relative z-10 w-full max-w-3xl mx-4 flex rounded-3xl overflow-hidden backdrop-blur-2xl"
                style={{
                    backgroundColor: 'rgba(10,28,40,0.85)',
                    border: '1px solid rgba(37,198,164,0.2)',
                    boxShadow: '0 0 60px rgba(37,198,164,0.08)',
                }}
            >
                {/* Left — Branding */}
                <div className="flex flex-col items-center justify-center flex-1 px-10 py-14 gap-6">
                    {/* Logo */}
                    <div className="logo-spin-border inline-flex items-center justify-center shrink-0" style={{}}>
                        <img
                            src={n4hLogo}
                            alt="app"
                            className="w-32 h-32 object-contain rounded-full"
                        />
                    </div>

                    {/* Title */}
                    <div className="text-center">
                        <h1 className="text-3xl tracking-wide" style={{ color: '#FFFFFF', fontFamily: 'Blare, sans-serif' }}>
                            Solid<span style={{ color: '#25C6A4' }}>.</span>Flow
                        </h1>
                        <h2 className="text-lg text-white mt-2" style={{ fontFamily: "'Stolzl', sans-serif" }}>
                            Hub de Integrações
                        </h2>
                    </div>
                </div>

                {/* Vertical divider */}
                <div className="flex items-center py-10">
                    <div
                        className="w-px h-full"
                        style={{
                            background: 'linear-gradient(to bottom, transparent, rgba(37,198,164,0.5) 30%, rgba(37,198,164,0.5) 70%, transparent)',
                        }}
                    ></div>
                </div>

                {/* Right — Login form */}
                <div className="flex flex-col justify-center flex-1 px-10 py-14">
                    <form className="space-y-5" onSubmit={handleLogin}>
                        {error && (
                            <div className="p-3 rounded-lg flex items-start gap-2" style={{ backgroundColor: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)' }}>
                                <span className="material-icons text-red-400 flex-shrink-0" style={{ fontSize: '18px' }}>error</span>
                                <p className="text-sm text-red-400">{error}</p>
                            </div>
                        )}

                        <div>
                            <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">Email</label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="off"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Digite seu email"
                                className="w-full px-4 py-3 rounded-xl focus:outline-none transition-all duration-200 text-white placeholder:text-white/30"
                                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '2px solid rgba(37,198,164,0.25)' }}
                                onFocus={e => e.target.style.borderColor = 'rgba(37,198,164,0.7)'}
                                onBlur={e => e.target.style.borderColor = 'rgba(37,198,164,0.25)'}
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-semibold text-white mb-2">Senha</label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Digite sua senha"
                                className="w-full px-4 py-3 rounded-xl focus:outline-none transition-all duration-200 text-white placeholder:text-white/30"
                                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '2px solid rgba(37,198,164,0.25)' }}
                                onFocus={e => e.target.style.borderColor = 'rgba(37,198,164,0.7)'}
                                onBlur={e => e.target.style.borderColor = 'rgba(37,198,164,0.25)'}
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <input
                                id="rememberMe"
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                className="w-4 h-4 rounded cursor-pointer"
                                style={{ accentColor: '#25C6A4' }}
                            />
                            <label htmlFor="rememberMe" className="text-sm cursor-pointer" style={{ color: 'rgba(255,255,255,0.6)' }}>
                                Lembrar neste dispositivo
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="w-full py-3 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02]"
                            style={{ background: 'linear-gradient(135deg, #25C6A4, #1aa085)', boxShadow: '0 4px 20px rgba(37,198,164,0.3)' }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 28px rgba(37,198,164,0.5)'}
                            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,198,164,0.3)'}
                        >
                            Entrar
                        </button>

                    </form>

                    <p className="text-center text-xs mt-6" style={{ color: 'rgba(255,255,255,0.25)' }}>
                        Powered by <span style={{ fontFamily: 'Blare, sans-serif', color: '#FFFFFF' }}>Solid<span style={{ color: '#25C6A4' }}>.</span>Flow</span>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoginPage
